import { Box, FormHelperText, Typography } from '@mui/material';
import { useState, useEffect, useLayoutEffect } from 'react';
import OtpInput from 'react-otp-input';
import { maskMobileNumber } from '../../utils/utilityFunctions';
import { useDispatch } from 'react-redux';
import { getUserInfo, verifyLoginOtp } from '../../redux-store/actions/auth';
import { useHistory } from 'react-router';
import { LoginLayout, OtpTimer } from '../commonComponents';
import { getOtpForInvestor, verifyOtpForInvestor } from '../../redux-store/actions/onBoarding';
import { Location } from 'history';
import { loginWithOtp } from '../../redux-store/actions/auth';
import {
  GetOtpForInvestorResponse,
  LoginWithOtpResponseBody,
} from '../../redux-store/types/api-types';
import { Formik } from 'formik';
import { MFSubmitButton } from '../../lib/formik';

const intialValues: { OTP: string } = {
  OTP: '',
};
const LOGIN_DATA_KEY = 'login_data';
const SESSION_WARNING_THRESHOLD_SECONDS = 60;
const SESSION_DURATION_SECONDS = 15 * 60;
const OTP_ERROR_ID = 'login-otp-validation-error';

export default function LoginOtp({
  location,
}: {
  location: Location<{
    mobile: string;
    phone: string;
    email: string;
    verificationToken: string;
    loginType: string;
    referenceId: string;
    countryCode: string;
    emailDecrypted: string;
  }>;
}): JSX.Element {
  const parsedLoginData = JSON.parse(sessionStorage.getItem(LOGIN_DATA_KEY) || '{}');
  const routeOrStoredState = (location.state || parsedLoginData) as {
    mobile?: string;
    phone?: string;
    email?: string;
    verificationToken?: string;
    loginType?: string;
    referenceId?: string;
    countryCode?: string;
    emailDecrypted?: string;
  };
  const {
    mobile = '',
    loginType = '',
    referenceId = '',
    countryCode = '+91',
    phone = '',
    email = '',
    emailDecrypted = '',
  } = routeOrStoredState;
  const dispatch = useDispatch();
  const history = useHistory();
  const [error, setError] = useState<string>('');
  const [{ verificationToken }, setVerifyOtpInfo] = useState({ verificationToken: '' });
  const [sessionCounter, setSessionCounter] = useState(SESSION_DURATION_SECONDS);

  const handleSubmit = async (values: { OTP: string }) => {
    const { OTP } = values;
    try {
      if (!OTP || OTP.length !== 6) {
        setError('Enter OTP');
        return;
      }
      if (loginType === 'investor') {
        await dispatch(verifyOtpForInvestor(referenceId, { otp: OTP, verificationToken }));
        sessionStorage.removeItem(LOGIN_DATA_KEY);
        await dispatch(getUserInfo());
        history.push(`application-details`);
        return;
      }
      await dispatch(verifyLoginOtp({ email: emailDecrypted, otp: OTP, verificationToken }));
      sessionStorage.removeItem(LOGIN_DATA_KEY);
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem(LOGIN_DATA_KEY);

    if (!location.state && stored) {
      const parsed = JSON.parse(stored);

      history.replace('verify-otp', {
        emailDecrypted: parsed.email,
        mobile: parsed.mobile,
        countryCode: parsed.countryCode,
        verificationToken: parsed.verificationToken,
        email: parsed.email,
        loginType: parsed.loginType,
        referenceId: parsed.referenceId,
      });

      setVerifyOtpInfo({ verificationToken: parsed.verificationToken || '' });
      return;
    }

    const { verificationToken } = routeOrStoredState;

    if (!verificationToken) {
      history.push('/login');
    }

    setVerifyOtpInfo({ verificationToken: verificationToken || '' });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionCounter((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    const resetCounter = () => setSessionCounter(SESSION_DURATION_SECONDS);
    window.addEventListener('mousemove', resetCounter);
    window.addEventListener('keydown', resetCounter);
    window.addEventListener('click', resetCounter);
    window.addEventListener('touchstart', resetCounter);
    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', resetCounter);
      window.removeEventListener('keydown', resetCounter);
      window.removeEventListener('click', resetCounter);
      window.removeEventListener('touchstart', resetCounter);
    };
  }, []);

  useLayoutEffect(() => {
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      const otpInputs = document.querySelectorAll<HTMLInputElement>('.otp-input input');
      otpInputs.forEach((input, index) => {
        input.setAttribute('aria-label', `OTP Digit ${index + 1}`);
        input.setAttribute('inputmode', 'numeric');
        input.setAttribute('autocomplete', 'one-time-code');
        if (index === 0) {
          if (error) {
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-describedby', OTP_ERROR_ID);
          } else {
            input.removeAttribute('aria-invalid');
            input.removeAttribute('aria-describedby');
          }
        }
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [error]);

  useEffect(() => {
    if (sessionCounter === 0) {
      sessionStorage.removeItem(LOGIN_DATA_KEY);
      history.push('/login');
    }
  }, [sessionCounter, history]);

  const onResend = async () => {
    try {
      if (loginType === 'investor') {
        const response = (await dispatch(
          getOtpForInvestor(referenceId)
        )) as unknown as GetOtpForInvestorResponse;
        setVerifyOtpInfo({ ...response });
        setSessionCounter(SESSION_DURATION_SECONDS);
        sessionStorage.setItem(
          LOGIN_DATA_KEY,
          JSON.stringify({
            ...routeOrStoredState,
            ...response,
            email: emailDecrypted,
          })
        );
        return;
      }
      const res = (await dispatch(
        loginWithOtp({ email: emailDecrypted })
      )) as unknown as LoginWithOtpResponseBody;
      setVerifyOtpInfo({ ...res });
      setSessionCounter(SESSION_DURATION_SECONDS);
      sessionStorage.setItem(
        LOGIN_DATA_KEY,
        JSON.stringify({
          ...routeOrStoredState,
          ...res,
          email: emailDecrypted,
        })
      );
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <LoginLayout LoginText="Login with OTP">
      <Formik initialValues={intialValues} onSubmit={handleSubmit}>
        {({ values, setValues, handleSubmit }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Typography sx={{ color: 'primary.main', textAlign: 'center', mb: 3.5 }}>
              An OTP has been sent to your{' '}
              {phone && <Typography component="span">{`mobile number ${phone}`}</Typography>}
              {phone && email && <Typography component="span">{` and `}</Typography>}
              {email && (
                <Typography component="span">
                  email Id <Typography component="span"> {email}</Typography>
                </Typography>
              )}
            </Typography>
            <Box
              sx={{
                mb: 0,
                '.otp-input': {
                  input: {
                    width: { xs: '37px !important', md: '45px !important' },
                    fontSize: { xs: '20px !important', md: '30px !important' },
                    margin: { xs: '2px !important', md: '4px !important' },
                  },
                },
              }}>
              <OtpInput
                value={values.OTP}
                onChange={(OTP: string) => setValues({ OTP })}
                numInputs={6}
                hasErrored={!!error}
                isInputNum={true}
                isInputSecure={true}
                shouldAutoFocus={true}
                containerStyle={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
                inputStyle={{
                  padding: '5px 10px',
                  // width: '45px',
                  borderRadius: '5px',
                  fontSize: '30px',
                }}
                errorStyle={{ border: '2px solid red', color: 'red' }}
                focusStyle={{
                  outline: '3px solid #2057A6',
                  outlineOffset: '2px',
                }}
                className="otp-input"
              />
              {!!error && (
                <FormHelperText
                  id={OTP_ERROR_ID}
                  error
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                  sx={{ mt: 2, textAlign: 'center', fontSize: '14px' }}>
                  {error}
                </FormHelperText>
              )}
            </Box>
            <Box
              sx={{
                '.MuiTypography-root, .MuiButton-root': {
                  textAlign: 'center',
                },
              }}>
              <OtpTimer onResend={onResend} />
              <Typography
                sx={{
                  mt: 1,
                  fontSize: 14,
                  color: 'text.secondary',
                  textAlign: 'center',
                }}>
                For security reasons, inactive sessions expire automatically after 15 minutes.
              </Typography>
              {sessionCounter <= SESSION_WARNING_THRESHOLD_SECONDS && (
                <Typography
                  role="status"
                  aria-live="assertive"
                  sx={{ mt: 1, color: 'warning.main' }}>
                  Session will expire in less than 1 minute due to inactivity.
                </Typography>
              )}
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <MFSubmitButton label="Submit" />
            </Box>
          </Box>
        )}
      </Formik>
    </LoginLayout>
  );
}
